// @ts-strict-ignore
import type { SyntheticEvent } from "react";
import { useEffect, useState } from "react";
import type { PixelCrop, PercentCrop } from "react-image-crop";
import ReactImageCrop, { centerCrop, makeAspectCrop } from "react-image-crop";

import "react-image-crop/dist/ReactCrop.css";
import { imageStyles } from "./ImageCrop.styles";

interface Props {
  id: string;
  src: string;
  isCropFinalized: boolean;
  onSave: (file: File) => void;
  onError: (errorMessage: string) => void;
}

const initialCrop: PercentCrop = {
  unit: "%",
  width: 50,
  height: 50,
  x: null,
  y: null,
};

const ImageCrop = ({ id, src, isCropFinalized, onSave, onError }: Props) => {
  const [crop, setCrop] = useState<PercentCrop | PixelCrop>(initialCrop);
  const [croppedImage, setCroppedImage] = useState<HTMLImageElement>(null);

  useEffect(() => {
    if (isCropFinalized) {
      (async () => {
        try {
          const finalImage: File = await getCroppedImage();
          onSave(finalImage);
        } catch (err) {
          onError(err);
        }
      })();
    }
  }, [isCropFinalized]);

  const handleImageLoaded = (event: SyntheticEvent<HTMLImageElement, Event>) => {
    const { width, height } = event.currentTarget;

    const newCrop = centerCrop(makeAspectCrop(initialCrop, 1, width, height), width, height);

    setCroppedImage(event.currentTarget);
    setCrop(newCrop);
  };

  const handleImageUpdate = (newCrop: PixelCrop) => {
    setCrop(newCrop);
  };

  const getCroppedImage = (): Promise<File> => {
    if (!croppedImage) {
      return new Promise(reject => reject(null));
    }

    const canvas = document.createElement("canvas");
    const scaleX = croppedImage.naturalWidth / croppedImage.width;
    const scaleY = croppedImage.naturalHeight / croppedImage.height;
    const context = canvas.getContext("2d");

    canvas.width = crop.width;
    canvas.height = crop.height;

    context.drawImage(
      croppedImage,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => {
          if (blob) {
            const blobAsFile: File = new File([blob], `${id}-avatar.png`, {
              type: "image/png",
              lastModified: new Date().getTime(),
            });

            window.URL.revokeObjectURL(src); // revoking the URL created for the original image
            resolve(blobAsFile);
          } else {
            reject("Something went wrong while cropping your image. Please try again!");
          }
        },
        "image/png",
        1.0
      );
    });
  };

  return (
    <ReactImageCrop
      aspect={1}
      css={imageStyles}
      circularCrop
      crop={crop}
      ruleOfThirds
      onChange={handleImageUpdate}
    >
      <img src={src} onLoad={handleImageLoaded} />
    </ReactImageCrop>
  );
};

export { ImageCrop };
